import { useState } from "react";
import { useWallet } from "@/lib/wallet";
import { Wallet, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function WalletButton({ variant = "ghost" }) {
  const { wallets, address, connect, disconnect } = useWallet();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const short = address ? `${address.slice(0, 4)}…${address.slice(-4)}` : null;

  const handle = async (w) => {
    setBusy(true);
    try {
      const a = await connect(w);
      toast.success("Wallet connected", { description: `${a.slice(0, 6)}…${a.slice(-4)}` });
    } catch (e) {
      toast.error(e?.message || "Wallet connection failed");
    }
    setBusy(false);
    setOpen(false);
  };

  const cls =
    variant === "solid"
      ? "inline-flex items-center gap-2 rounded-full bg-sgreen px-5 py-2.5 text-sm font-semibold text-black disabled:opacity-60"
      : "inline-flex items-center gap-2 rounded-full border border-line bg-white/[0.03] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:border-sgreen/50";

  if (address) {
    return (
      <button onClick={disconnect} data-testid="wallet-disconnect-button" className={cls} title="Disconnect wallet">
        {short}
        <LogOut className="h-3.5 w-3.5 text-mut" />
      </button>
    );
  }

  if (wallets.length === 0) {
    return (
      <a
        href="https://phantom.app"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="wallet-install-link"
        className={cls}
        title="No Solana wallet detected — install Phantom"
      >
        <Wallet className="h-4 w-4 text-sgreen" />
        Install Wallet
      </a>
    );
  }

  if (wallets.length === 1) {
    return (
      <button onClick={() => handle(wallets[0])} disabled={busy} data-testid="wallet-connect-button" className={cls}>
        <Wallet className="h-4 w-4 text-sgreen" />
        {busy ? "Connecting…" : "Connect Wallet"}
      </button>
    );
  }

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="wallet-connect-button" className={cls}>
        <Wallet className="h-4 w-4 text-sgreen" />
        Connect Wallet
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-48 rounded-xl border border-line bg-card2 p-2 shadow-2xl">
          {wallets.map((w) => (
            <button
              key={w.name}
              onClick={() => handle(w)}
              data-testid={`wallet-option-${w.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="w-full rounded-lg px-3 py-2.5 text-left text-sm text-white hover:bg-white/5"
            >
              {w.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
